import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';

import GroupEmptyList from './components/GroupEmptyList';
import { List, Container } from './styles';

interface Props {
  groupTarget?: string;
  groupList: GroupedListModel[];
  isLoading: boolean;
  tourHasAccess: boolean;
  tourLoading: boolean;
  ruleSelected?: GroupedListModel;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (rule: GroupedListModel) => void;
}

const GroupList = ({ groupList, isLoading, tourHasAccess, tourLoading }: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [groupResult, setRulesResult] = useState<GroupedListModel[]>([]);
  const history = useHistory();
  const { t } = useStoreTranslation();

  const handleSelection = (group: GroupedListModel) => {
    history.push(`/corporate/group/${group.id}`);
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setShowSearch(true);
      setRulesResult(
        groupList.filter(
          (name: GroupedListModel) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else {
      setShowSearch(false);
    }
  };

  const renderList = () => (
    <>
      <Search show={groupList.length >= 10} onSearch={handleOnSearch} />

      <Mobile>
        {showSearch ? (
          <List>
            <GroupedItem
              quantityText={htmlT(t, 'groupList.searchResults', { count: groupResult?.length })}
              isSearch={showSearch}
              items={groupResult}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </List>
        ) : (
          <>
            <GroupedList
              isAlphabetic
              isSelectable={false}
              items={groupList}
              isSearch={showSearch}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </>
        )}
      </Mobile>

      <Desktop>
        <List>
          {(showSearch ? groupResult : groupList).map((group) => (
            <Item
              key={`user-${group.id}`}
              title={group.name || ''}
              onClick={() => handleSelection(group)}
              badgesContent={group.badges}
            />
          ))}
        </List>
      </Desktop>
    </>
  );
  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('groupList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.push('/corporate/dashboard')}>
          <h5>{t('groupList.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('userMenu.groupsTitle')} subtitle={t('userMenu.groupsDetail')} />
          </Desktop>

          <Container hasList={groupList.length > 0}>
            <Tour
              type="cancelable"
              show={!tourLoading && !tourHasAccess}
              hideArrow
              steps={initialSteps}
              disableOverlayClose
              disableScrollParentFix
            />
            {groupList && groupList.length ? renderList() : <GroupEmptyList />}
          </Container>
        </>
      )}
      <FloatingButton zIndex={2} onClick={() => history.push('/corporate/group')} className="target" />
    </>
  );
};

export default GroupList;
